import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { SxProps, Theme } from '@mui/material/styles'
import { ScanIcon } from '../_icons/ScanIcon'
import { scanSearchPanelStyles as s } from './ScanSearchPanel.styles'
export interface ScanOption {
  key: string
  label: string
  placeholder: string
}

interface ScanSearchPanelProps {
  /** List of toggle options. Each adds one tab to the switch. */
  options: ScanOption[]
  /** Called when the user clicks "Buscar" or presses Enter. */
  onSearch: (value: string, option: ScanOption) => void
  /** Label displayed above the panel. */
  hintLabel?: string
  /** Text for the search button. */
  searchLabel?: string
  /** Disable the whole panel (e.g. while loading). */
  disabled?: boolean
  /** Extra sx overrides on the outer container. */
  sx?: SxProps<Theme>
  // Para mantener la referencia al autofocus del input
  inputRef?: React.RefObject<HTMLInputElement>
}

const ScanSearchPanel: React.FC<ScanSearchPanelProps> = ({
  options,
  onSearch,
  hintLabel = 'Ingresa o escanea el dato',
  searchLabel = 'Buscar',
  disabled = false,
  sx,
  inputRef: externalRef
}) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [inputValue, setInputValue] = useState('')
  const [sliderStyle, setSliderStyle] = useState<{
    left: number
    width: number
  }>({ left: 0, width: 0 })

  const toggleGroupRef = useRef<HTMLDivElement | null>(null)
  const btnRefs = useRef<(HTMLButtonElement | null)[]>([])
  const internalRef = useRef<HTMLInputElement | null>(null)
  const inputRef = externalRef ?? internalRef

  const updateSlider = useCallback(() => {
    const group = toggleGroupRef.current
    const btn = btnRefs.current[activeIndex]
    if (!group || !btn) return
    const gRect = group.getBoundingClientRect()
    const bRect = btn.getBoundingClientRect()
    setSliderStyle({ left: bRect.left - gRect.left, width: bRect.width })
  }, [activeIndex])

  useEffect(() => {
    updateSlider()

    const observer = new ResizeObserver(() => updateSlider())
    if (toggleGroupRef.current) observer.observe(toggleGroupRef.current)

    window.addEventListener('resize', updateSlider)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', updateSlider)
    }
  }, [updateSlider])

  useEffect(() => {
    inputRef.current?.focus()
  }, [activeIndex])

  const handleSelect = (index: number) => {
    setActiveIndex(index)
    setInputValue('')
  }

  const handleSearch = useCallback(() => {
    const trimmed = inputValue.trim()
    if (!trimmed || disabled) return
    onSearch(trimmed, options[activeIndex])
  }, [inputValue, activeIndex, disabled, onSearch, options])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch()
  }

  const activeOption = options[activeIndex] ?? options[0]

  return (
    <Stack spacing={1.5} sx={sx}>
      <Typography variant='h6' component='h1' sx={s.hintLabel}>
        {hintLabel}
      </Typography>

      <Box sx={s.card}>
        {/* Toggle switch */}
        <Box ref={toggleGroupRef} sx={s.toggleGroup}>
          <Box sx={s.toggleSlider(sliderStyle.left, sliderStyle.width)} />
          {options.map((opt, i) => (
            <Box
              key={opt.key}
              component='button'
              ref={(el: HTMLButtonElement | null) => {
                btnRefs.current[i] = el
              }}
              onClick={() => handleSelect(i)}
              disabled={disabled}
              sx={s.toggleBtn(i === activeIndex)}
            >
              {opt.label}
            </Box>
          ))}
        </Box>

        {/* Text input */}
        <TextField
          inputRef={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={activeOption?.placeholder ?? ''}
          disabled={disabled}
          autoComplete='off'
          fullWidth
          size='small'
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <ScanIcon />
              </InputAdornment>
            ),
            sx: s.inputRoot
          }}
          inputProps={{ sx: s.inputProps, autoFocus: true }}
        />

        {/* Search button */}
        <Button
          variant='contained'
          fullWidth
          disabled={!inputValue.trim() || disabled}
          onClick={handleSearch}
          disableElevation
        >
          {searchLabel}
        </Button>
      </Box>
    </Stack>
  )
}

export default ScanSearchPanel
